import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Configure Supabase client to prevent aggressive auto-refresh on visibility change
// This prevents page reloads when users switch tabs
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true, // Keep enabled for security, but we'll control when it refreshes
    persistSession: true,
    detectSessionInUrl: false, // Prevent URL-based session detection that can cause reloads
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce', // Use PKCE flow for better security
  },
  global: {
    // Prevent automatic retries that might cause reloads
    fetch: (url, options = {}) => {
      // If a signal is already provided, use it (don't create a new one)
      if (options.signal) {
        return fetch(url, options)
      }
      
      // Add timeout to prevent hanging requests
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 60000) // 60 second timeout for RPC calls
      
      return fetch(url, {
        ...options,
        signal: controller.signal,
      }).finally(() => {
        clearTimeout(timeoutId)
      })
    },
  },
})

// CRITICAL FIX: Prevent Supabase from causing reloads on visibility change
// Supabase internally listens to visibilitychange and calls _recoverAndRefresh
// which triggers TOKEN_REFRESHED events. We prevent these from causing state changes.
if (typeof window !== 'undefined') {
  // Track when tab was last hidden to prevent unnecessary refreshes on quick switches
  let lastTabHidden = null
  
  // Intercept visibility change to track tab state
  // This helps us determine if a refresh is necessary (only after long absences)
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      // Tab hidden - record the time
      lastTabHidden = Date.now()
      sessionStorage.setItem('lastTabHidden', lastTabHidden.toString())
    } else if (document.visibilityState === 'visible') {
      // Tab visible - check if we should allow Supabase refresh
      const hiddenDuration = lastTabHidden ? Date.now() - lastTabHidden : 0
      
      // Only log for debugging - Supabase will refresh anyway, but we prevent
      // our state from changing unless tab was hidden for more than 5 minutes
      if (hiddenDuration < 5 * 60 * 1000) {
        // Quick tab switch - Supabase may refresh, but we've configured
        // onAuthStateChange handlers to ignore TOKEN_REFRESHED events
        console.debug('Quick tab switch detected - preventing state changes from token refresh')
      }
      
      lastTabHidden = null
      sessionStorage.removeItem('lastTabHidden')
    }
  }
  
  // Add handler to track visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

